import React from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { User, ChevronLeft, ChevronRight } from 'lucide-react'

interface Etapa1Props {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
  onSubmit: () => void
  saving: boolean
  submitting: boolean
}

export function Etapa1Identificacao({ formData, onChange, onNext, onPrev, onSubmit, saving, submitting }: Etapa1Props) {

  // Função de validação com feedback
  const validateAndProceed = () => {
    const errors: string[] = []

    if (!formData.nome_completo?.trim()) errors.push('Nome completo')
    if (!formData.data_nascimento) errors.push('Data de nascimento')
    if (!formData.cpf?.trim()) errors.push('CPF')

    if (errors.length > 0) {
      alert(`Por favor, preencha os campos obrigatórios:\n\n${errors.join('\n')}`)
      return
    }

    onNext()
  }
  
  // Estado local para campos de data
  const [diaSelecionado, setDiaSelecionado] = React.useState('')
  const [mesSelecionado, setMesSelecionado] = React.useState('')
  const [anoSelecionado, setAnoSelecionado] = React.useState('')
  
  // Sincronizar estado local com formData quando mudar
  React.useEffect(() => {
    if (formData.data_nascimento) {
      const partes = formData.data_nascimento.split('-')
      if (partes.length === 3 && partes[0] !== '0000') {
        setAnoSelecionado(partes[0])
        setMesSelecionado(partes[1])
        setDiaSelecionado(partes[2])
      } else if (partes[0] === '0000') {
        setDiaSelecionado(partes[2])
        setMesSelecionado(partes[1])
        setAnoSelecionado('')
      }
    } else {
      setDiaSelecionado('')
      setMesSelecionado('')
      setAnoSelecionado('')
    }
  }, [formData.data_nascimento])
  
  const calcularIdade = (dataNascimento: string) => {
    if (!dataNascimento) return null
    
    const nascimento = new Date(dataNascimento)
    const hoje = new Date()
    let idade = hoje.getFullYear() - nascimento.getFullYear()
    const mes = hoje.getMonth() - nascimento.getMonth()
    
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--
    }
    
    return idade
  }


  const idade = calcularIdade(formData.data_nascimento)

  return (
    <div className="space-y-6">
      {/* Informações Básicas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Dados do Idoso(a)
            {idade && (
              <span className="ml-2 text-sm font-bold text-blue-600">
                {idade} anos
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <Label htmlFor="nome_completo" className="block mb-2">Nome Completo *</Label>
              <Input
                id="nome_completo"
                value={formData.nome_completo || ''}
                onChange={(e) => onChange('nome_completo', e.target.value)}
                placeholder="Nome completo do idoso(a)"
              />
            </div>
            
            <div>
              <Label htmlFor="data_nascimento" className="block mb-2">Data de Nascimento *</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select 
                    value={diaSelecionado} 
                    onValueChange={(value) => {
                      setDiaSelecionado(value)
                      // Atualizar formData quando todos os campos estiverem preenchidos
                      if (value && mesSelecionado && anoSelecionado) {
                        onChange('data_nascimento', `${anoSelecionado}-${mesSelecionado}-${value.padStart(2, '0')}`)
                      } else if (value && mesSelecionado && !anoSelecionado) {
                        onChange('data_nascimento', `0000-${mesSelecionado}-${value.padStart(2, '0')}`)
                      } else {
                        onChange('data_nascimento', '')
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Dia" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 31}, (_, i) => i + 1).map(dia => (
                        <SelectItem key={dia} value={dia.toString()}>
                          {dia}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex-1">
                  <Select 
                    value={mesSelecionado} 
                    onValueChange={(value) => {
                      setMesSelecionado(value)
                      // Atualizar formData quando todos os campos estiverem preenchidos
                      if (diaSelecionado && value && anoSelecionado) {
                        onChange('data_nascimento', `${anoSelecionado}-${value}-${diaSelecionado}`)
                      } else if (diaSelecionado && value && !anoSelecionado) {
                        onChange('data_nascimento', `0000-${value}-${diaSelecionado}`)
                      } else {
                        onChange('data_nascimento', '')
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Mês" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="01">Jan</SelectItem>
                      <SelectItem value="02">Fev</SelectItem>
                      <SelectItem value="03">Mar</SelectItem>
                      <SelectItem value="04">Abr</SelectItem>
                      <SelectItem value="05">Mai</SelectItem>
                      <SelectItem value="06">Jun</SelectItem>
                      <SelectItem value="07">Jul</SelectItem>
                      <SelectItem value="08">Ago</SelectItem>
                      <SelectItem value="09">Set</SelectItem>
                      <SelectItem value="10">Out</SelectItem>
                      <SelectItem value="11">Nov</SelectItem>
                      <SelectItem value="12">Dez</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex-1">
                  <Select 
                    value={anoSelecionado} 
                    onValueChange={(value) => {
                      setAnoSelecionado(value)
                      // Atualizar formData quando todos os campos estiverem preenchidos
                      if (diaSelecionado && mesSelecionado && value) {
                        onChange('data_nascimento', `${value}-${mesSelecionado}-${diaSelecionado}`)
                      } else if (diaSelecionado && mesSelecionado && !value) {
                        onChange('data_nascimento', `0000-${mesSelecionado}-${diaSelecionado}`)
                      } else {
                        onChange('data_nascimento', '')
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Ano" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 100}, (_, i) => new Date().getFullYear() - i).map(ano => (
                        <SelectItem key={ano} value={ano.toString()}>
                          {ano}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
                          </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cpf">CPF *</Label>
              <Input
                id="cpf"
                value={formData.cpf || ''}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, '')
                  if (value.length > 11) value = value.slice(0, 11)
                  if (value.length > 9) {
                    value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
                  } else if (value.length > 6) {
                    value = value.replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3')
                  } else if (value.length > 3) {
                    value = value.replace(/(\d{3})(\d{3})/, '$1.$2')
                  }
                  onChange('cpf', value)
                }}
                placeholder="000.000.000-00"
                className="mt-1"
                maxLength={14}
              />
            </div>
            
            <div>
              <Label htmlFor="rg">RG</Label>
              <Input
                id="rg"
                value={formData.rg || ''}
                onChange={(e) => onChange('rg', e.target.value)}
                placeholder="RG (sem formatação)"
                className="mt-1"
              />
            </div>
          </div>

          
          <div>
            <Label htmlFor="convenio">Convênio/Plano de Saúde</Label>
            <Input
              id="convenio"
              value={formData.convenio || ''}
              onChange={(e) => onChange('convenio', e.target.value)}
              placeholder="Nome do convênio ou plano de saúde (se houver)"
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Navegação */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev} disabled>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <Button onClick={validateAndProceed}>
          Próxima Etapa
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
